import { z } from "zod/v4";

export const DateTimeSchema = z
  .string()
  .datetime({ offset: true })
  .transform((val) => new Date(val));

export type Device = z.infer<typeof DeviceSchema>;
export const DeviceSchema = z.object({
  id: z.string(),
  discardedAt: DateTimeSchema.nullable(),
  hardwareVersion: z.string().nullable(),
  location: z.string().nullable(),
  serialNumber: z.string(),
  softwareVersion: z.string().nullable(),
  timezone: z.string().nullable(),
  tenantId: z.string().nullable(),
  name: z.string().nullable(),
  displayName: z.string(),
  contactPerson: z.string().nullable(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
  lastSeen: z.string().nullable(),
  deviceModel: z
    .object({
      id: z.string(),
      name: z.string(),
      doubleStage: z.boolean(),
      features: z.enum(["base", "loop_tank", "loop_tank_and_heater"]),
    })
    .nullable(),
});

export type DisplayParameter = z.infer<typeof DisplayParameterSchema>;
export const DisplayParameterSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.number().nullable(),
  unit: z.string().nullable(),
});

export type DeviceEvent = z.infer<typeof DeviceEventSchema>;
export const DeviceEventSchema = z.object({
  id: z.string(),
  manifestedAt: DateTimeSchema,
  eventType: z.string().nullable(),
  eventGroup: z.string().nullable(),
  eventOperation: z.string().nullable(),
  displayParameters: z.array(DisplayParameterSchema).nullable().optional(),
  device: DeviceSchema,
});

export type Role = z.infer<typeof RoleSchema>;
export const RoleSchema = z.enum(["administrator", "technician", "system"]);

export type User = z.infer<typeof UserSchema>;
export const UserSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  username: z.string(),
  email: z.string(),
  phoneNumber: z.string().nullable().optional(),
  locale: z.string(),
  tenantSlug: z.string().nullable(),
  role: RoleSchema,
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
  doNotDisturbPeriodForEmail: z.string().nullable().optional(),
  alertTypesToReceive: z.enum(["warning", "warning_and_notification"]).default("warning"),
  emailSubscribedDeviceIds: z.array(z.string()).optional(),
  smsSubscribedDeviceIds: z.array(z.string()).optional(),
});

export type DeviceDocumentUploader = z.infer<typeof DeviceDocumentUploaderSchema>;
export const DeviceDocumentUploaderSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});

export type UserToken = z.infer<typeof UserTokenSchema>;
export const UserTokenSchema = z.object({
  access: z.string(),
  refresh: z.string().optional(),
});

export type Credentials = z.infer<typeof CredentialsSchema>;
export const CredentialsSchema = z.object({
  username: z.string(),
  password: z.string(),
});

export type Locale = z.infer<typeof LocaleSchema>;
export const LocaleSchema = z.object({
  key: z.string(),
  displayName: z.string(),
  localeDisplayName: z.string(),
});

export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;
export const PaginationMetaSchema = z.object({
  currentPage: z.number(),
  pageSize: z.number(),
  totalCount: z.number(),
  totalPages: z.number(),
});

// Use ErrorT instead of Error to avoid conflicts with the built-in Error type
export type ErrorT = z.infer<typeof ErrorSchema>;
export const ErrorSchema = z.object({
  title: z.string(),
  detail: z.string(),
  errorKey: z.string().nullable(),
  source: z.string().nullable(),
});

const DeviceModelSensorSchema = z.object({
  id: z.string().uuid(),
  defaultSystemMax: z.number().nullable().optional(),
  defaultSystemMin: z.number().nullable().optional(),
  defaultTenantMax: z.number().nullable().optional(),
  defaultTenantMin: z.number().nullable().optional(),
  displayFormat: z.string().nullable(),
  name: z.string().nullable(),
  sensorName: z.string().nullable(),
  sensorType: z.string().nullable(),
  systemAlarm: z.boolean().optional(),
  tenantAlarm: z.boolean().optional(),
  type: z.string().nullable(),
  unit: z.string().nullable(),
  actuator: z.boolean().optional(),
  digitalSensor: z.boolean().optional(),
  stateLabels: z
    .array(
      z.object({
        label: z.string(),
        value: z.number(),
        displayOrder: z.number(),
      }),
    )
    .nullish(),
});

export type DeviceSensor = z.infer<typeof DeviceSensorSchema>;
export const DeviceSensorSchema = z.object({
  id: z.string().uuid(),
  value: z.number().nullable().optional(),
  sensorName: z.string(),
  type: z.string().nullable(),
  tenantMin: z.number().nullable(),
  tenantMax: z.number().nullable(),
  tenantAlarm: z.boolean(),
  tenantAlertOnState: z.enum(["off", "low", "high"]),
  systemMin: z.number().nullable().optional(),
  systemMax: z.number().nullable().optional(),
  systemAlarm: z.boolean().optional(),
  systemAlertOnState: z.enum(["off", "low", "high"]).optional(),
  modelSensor: DeviceModelSensorSchema.nullish(),
});

export type Tenant = z.infer<typeof TenantSchema>;
export const TenantSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  address: z.string().nullable(),
  contactPerson: z.string().nullable(),
  country: z.string().nullable(),
  locale: z.string(),
  contactPhoneNumber: z.string().nullable(),
  timezone: z.string().nullable(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
  enableTelemetryApi: z.boolean(),
});

export type DeviceAlarm = z.infer<typeof DeviceAlarmSchema>;
export const DeviceAlarmSchema = z.object({
  id: z.string(),
  type: z.string(),
  manifestedAt: DateTimeSchema,
  boundType: z.string().nullable(),
  sensorName: z.string().nullable(),
  eventType: z.string().nullable().optional(),
  displayIncidentName: z.string().nullable().optional(),
  device: z.object({
    id: z.string(),
    name: z.string().nullable(),
  }),
  tenant: z.object({
    id: z.string(),
    slug: z.string(),
  }),
});

export type DeviceDocument = z.infer<typeof DocumentSchema>;
export const DocumentSchema = z.object({
  id: z.string(),
  deviceId: z.string(),
  name: z.string(),
  note: z.string().nullable().optional(),
  url: z.string().nullable(),
  type: z.string().nullable(),
  visibleToTenant: z.boolean(),
  documentType: z.string(),
  payload: z
    .object({
      format: z.enum(["csv", "xlsx", "pdf"]),
      startDate: DateTimeSchema,
      endDate: DateTimeSchema,
    })
    .nullish(),
  uploadedBy: DeviceDocumentUploaderSchema,
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
  file: z.instanceof(File).optional(),
  state: z.enum(["processing", "completed", "failed", "unknown"]).nullable().optional(),
});

export type DeviceSensorMetrics = z.infer<typeof DeviceSensorMetricsSchema>;
export const DeviceSensorMetricsSchema = z.object({
  timestamp: DateTimeSchema,
  metrics: z.record(z.string(), z.number()),
});

export type DeviceDisconnectionEvent = z.infer<typeof DeviceDisconnectionEventSchema>;
export const DeviceDisconnectionEventSchema = z.object({
  disconnectedAt: DateTimeSchema,
  reconnectedAt: DateTimeSchema.nullable(),
});

export type GenerateReportRequest = z.infer<typeof GenerateReportRequestSchema>;
export const GenerateReportRequestSchema = z.object({
  deviceId: z.string(),
  name: z.string(),
  note: z.string().nullable().optional(),
  type: z.string(),
  visibleToTenant: z.boolean(),
  documentType: z.string(),
  startTime: z.date().optional(),
  endTime: z.date().optional(),
  eventTypes: z.array(z.string()).optional(),
  eventGroups: z.array(z.string()).optional(),
  eventOperations: z.array(z.string()).optional(),
  sensorIds: z.array(z.string()).optional(),
  includeGraphs: z.boolean().optional(),
  timeInterval: z.string().optional(),
  format: z.enum(["xlsx", "csv", "pdf"]).default("xlsx"),
});

export type DownloadPayloadRequest = z.infer<typeof DownloadPayloadRequestSchema>;
export const DownloadPayloadRequestSchema = z.object({
  deviceId: z.string(),
  startDate: z.string(),
  endDate: z.string(),
});

export type DeviceMetricState = z.infer<typeof DeviceMetricStateSchema>;
export const DeviceMetricStateSchema = z.enum([
  "standby",
  "bypass_flow",
  "loop_flow",
  "emergency_mode_first_stage",
  "emergency_mode_second_stage",
  "emergency_mode_both_stages",
  "recirculation",
  "hot_water_preparation",
  "cold_water_flushing",
  "emptying_tank",
  "loop_cool_down",
  "heating_up_after_loop_hd",
  "heating_up_after_loop_hd_inline_heater",
  "disinfection",
  "cooling_down",
  "rinse",
]);

export type DeviceLatestMetric = z.infer<typeof DeviceLatestMetricSchema>;
export const DeviceLatestMetricSchema = z.object({
  id: z.string(),
  operatingState: DeviceMetricStateSchema,
  measuredAt: DateTimeSchema,
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
  serverTime: DateTimeSchema,
  connectionTimeoutSeconds: z.number().int().positive(),
  deviceClockOffsetSeconds: z.number(),
  lastReceivedAt: DateTimeSchema,
  payload: z.record(z.string(), z.number()).optional(),
});

export type DeviceEventFilter = z.infer<typeof DeviceEventFilterSchema>;
export const DeviceEventFilterSchema = z.object({
  name: z.string(),
  displayName: z.string(),
});

export type DeviceEventFilters = z.infer<typeof DeviceEventFiltersSchema>;
export const DeviceEventFiltersSchema = z.object({
  eventTypes: z.array(DeviceEventFilterSchema),
  eventGroupTypes: z.array(DeviceEventFilterSchema),
  eventOperations: z.array(DeviceEventFilterSchema),
});

export const TelemetryAPIKeySchema = z.object({
  id: z.string().uuid(),
  expirationAt: DateTimeSchema,
  maxExpirationAt: DateTimeSchema,
  expirationPeriod: z.string(),
  name: z.string(),
  truncatedKey: z.string(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
  rawKey: z.string().nullable().optional(),
  lastUsedAt: DateTimeSchema.nullable(),
  rolledOverToId: z.string().uuid().nullable(),
});

export type TelemetryAPIKey = z.infer<typeof TelemetryAPIKeySchema>;
export const TelemetryRollOverAPIKeySchema = z.object({
  id: z.string().uuid(),
  graceTime: DateTimeSchema,
});

export type TelemetryRollOverAPIKey = z.infer<typeof TelemetryRollOverAPIKeySchema>;

export type ReportSchedule = z.infer<typeof ReportScheduleSchema>;
export const ReportScheduleSchema = z.object({
  id: z.string(),
  reportType: z.string(),
  name: z.string(),
  frequency: z.enum(["daily", "weekly", "monthly"]),
  dayOfWeek: z.number().nullable(),
  dayOfMonth: z.number().nullable(),
  format: z.enum(["xlsx", "csv", "pdf"]),
  reportConfiguration: z.object({
    eventTypes: z.array(z.string()).nullable().optional(),
    eventGroups: z.array(z.string()).nullable().optional(),
    eventOperations: z.array(z.string()).nullable().optional(),
    sensorIds: z.array(z.string()).nullable().optional(),
    timeInterval: z.string().nullable().optional(),
    includeGraphs: z.boolean().optional(),
  }),
  visibleToTenant: z.boolean(),
  deliveryTime: z.string(),
  deviceId: z.string(),
  userIds: z.array(z.string()),
  note: z.string().nullable().optional(),
});

// Device Counter schemas
export const DeviceCounterSchema = z.object({
  id: z.string(),
  updatedAt: DateTimeSchema,
  counters: z.array(
    z.object({
      key: z.string(),
      value: z.number().nullable(),
      unit: z.string().nullable(),
    }),
  ),
});

export const DeviceCounterResponseSchema = z.object({
  deviceCounter: DeviceCounterSchema.nullable(),
});

export type DeviceCounter = z.infer<typeof DeviceCounterSchema>;
export type DeviceCounterResponse = z.infer<typeof DeviceCounterResponseSchema>;
